import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import styled, { keyframes } from 'styled-components';
import {
  CheckCircle,
  Download,
  ArrowRight,
  AlertTriangle,
  Ticket,
  Calendar,
  CreditCard,
  Loader2,
  Mail,
  Clock
} from 'lucide-react';
import api from '../services/api';
import { useCart } from '../store/cartStore';

/* --- CINEMATIC GLASS PALETTE --- */
const palette = {
  shell: 'radial-gradient(circle at 50% 0%, #1a1d2d 0%, #040509 100%)',
  panel: 'rgba(20, 22, 32, 0.6)',
  panelBorder: 'rgba(255, 255, 255, 0.08)',
  ink: '#f5f8ff',
  muted: 'rgba(245, 248, 255, 0.65)',
  accent: '#3b82f6',
  accentGlow: '0 0 20px rgba(59, 130, 246, 0.4)',
  success: '#10b981',
  successGlow: '0 0 20px rgba(16, 185, 129, 0.25)',
  surface: 'rgba(255, 255, 255, 0.03)',
  surfaceHover: 'rgba(255, 255, 255, 0.06)'
};

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageShell = styled.div`
  min-height: 100vh;
  padding: 2rem;
  padding-top: 120px; /* Ensure clearance for fixed navbar */
  background: ${palette.shell};
  display: flex;
  justify-content: center;
  align-items: flex-start; /* prevent overlap on small screens by allowing scroll */
  color: ${palette.ink};
  font-family: 'Inter', sans-serif;
`;

const ContentCard = styled.div`
  width: min(720px, 100%);
  background: ${palette.panel};
  border: 1px solid ${palette.panelBorder};
  border-radius: 32px;
  padding: 3rem;
  backdrop-filter: blur(40px);
  -webkit-backdrop-filter: blur(40px);
  box-shadow: 0 40px 80px -20px rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  gap: 2rem;
  animation: ${fadeIn} 0.6s cubic-bezier(0.2, 0.8, 0.2, 1);
  position: relative;
  overflow: hidden;

  /* Ambient glow effect */
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.08), transparent 70%);
    pointer-events: none;
    z-index: 0;
  }
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1.5rem;
  position: relative;
  z-index: 1;
`;

const IconBadge = styled.div`
  width: 96px;
  height: 96px;
  border-radius: 50%;
  background: ${({ $type }) => $type === 'error' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)'};
  border: 1px solid ${({ $type }) => $type === 'error' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(16, 185, 129, 0.2)'};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${({ $type }) => $type === 'error' ? '0 0 30px rgba(239, 68, 68, 0.15)' : palette.successGlow};
  margin-bottom: 0.5rem;

  svg {
    width: 48px;
    height: 48px;
    color: ${({ $type }) => $type === 'error' ? '#ef4444' : palette.success};
    filter: drop-shadow(0 0 8px ${({ $type }) => $type === 'error' ? 'rgba(239, 68, 68, 0.4)' : 'rgba(16, 185, 129, 0.4)'});
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 2.2rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  background: linear-gradient(135deg, #fff 0%, #a5b4fc 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  margin: 0;
  color: ${palette.muted};
  font-size: 1.1rem;
  line-height: 1.6;
  max-width: 480px;
`;

const OrderSummaryCard = styled.div`
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 24px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  position: relative;
  z-index: 1;
`;

const SummaryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  h2 {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const StatusPill = styled.span`
  padding: 0.35rem 0.85rem;
  border-radius: 99px;
  background: ${({ $status }) =>
    $status === 'PAID' || $status === 'COMPLETED' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(245, 158, 11, 0.15)'};
  color: ${({ $status }) =>
    $status === 'PAID' || $status === 'COMPLETED' ? '#34d399' : '#fbbf24'};
  border: 1px solid ${({ $status }) =>
    $status === 'PAID' || $status === 'COMPLETED' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(245, 158, 11, 0.2)'};
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const DataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const DataItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;

  label {
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.4);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }

  span {
    font-size: 1.1rem;
    font-weight: 500;
    color: #fff;
  }
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  font-size: 1.2rem;
  
  span:last-child {
    font-weight: 700;
    color: ${palette.accent};
    text-shadow: 0 0 20px rgba(59, 130, 246, 0.3);
  }
`;

const ActionRow = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 1rem;
  margin-top: 0.5rem;
  position: relative;
  z-index: 1;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: 1rem;
  border-radius: 16px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.2, 0.8, 0.2, 1);
  border: 1px solid ${({ $variant }) => $variant === 'primary' ? 'transparent' : 'rgba(255, 255, 255, 0.1)'};
  background: ${({ $variant }) =>
    $variant === 'primary'
      ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
      : 'rgba(255, 255, 255, 0.05)'};
  color: ${({ $variant }) => $variant === 'primary' ? '#fff' : palette.ink};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    background: ${({ $variant }) =>
    $variant === 'primary'
      ? 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)'
      : 'rgba(255, 255, 255, 0.1)'};
    box-shadow: ${({ $variant }) =>
    $variant === 'primary' ? '0 10px 25px -5px rgba(59, 130, 246, 0.5)' : 'none'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: wait;
  }
`;

const InfoBox = styled.div`
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 20px;
  padding: 1.25rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  font-size: 0.95rem;
  line-height: 1.5;
  color: #bfdbfe;
  position: relative;
  z-index: 1;

  svg {
    flex-shrink: 0;
    margin-top: 0.1rem;
  }
`;

const SupportLink = styled.button`
  background: none;
  border: none;
  color: ${palette.muted};
  font-size: 0.9rem;
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: rgba(255, 255, 255, 0.2);
  transition: color 0.2s;
  padding: 0;
  margin-top: 1rem;
  align-self: center;

  &:hover {
    color: #fff;
  }
`;

const CheckoutSuccessPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loadCartFromBackend } = useCart();

  const [isLoading, setIsLoading] = useState(true);
  const [orderData, setOrderData] = useState(null);
  const [error, setError] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [pollAttempts, setPollAttempts] = useState(0);
  const [downloadingTickets, setDownloadingTickets] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  const sessionId = searchParams.get('session_id');
  const { orderId } = location.state || {};
  const MAX_POLL_ATTEMPTS = 8;
  const POLL_INTERVAL = 2500;
  const resolvedOrderIdRef = useRef(orderId || null);
  const [displayOrderId, setDisplayOrderId] = useState(orderId || null);

  useEffect(() => {
    let timeoutId;
    let cancelled = false;

    const handleCheckoutSuccess = async () => {
      let shouldRepoll = false;
      try {
        setIsLoading(true);

        if (sessionId) {
          const response = await api.get(`/payments/checkout/success?sessionId=${sessionId}`);
          if (response.orderId) {
            resolvedOrderIdRef.current = response.orderId;
            setDisplayOrderId(response.orderId);
          }

          if (response.success) {
            const targetOrderId = response.orderId || resolvedOrderIdRef.current;
            if (!targetOrderId) {
              throw new Error('Order ID missing from payment success response');
            }
            const orderResponse = await api.get(`/orders/${targetOrderId}`);
            if (cancelled) return;
            setOrderData(orderResponse);
            localStorage.removeItem('pendingOrderId');
            await loadCartFromBackend();
            setStatusMessage('');
            setPollAttempts(0);
          } else {
            const responseCode = response.code;
            const responseMessage = response.message;
            const orderStatus = response.orderStatus;
            const orderPaid = orderStatus === 'PAID' || orderStatus === 'COMPLETED';
            const sessionStatus = (response.sessionStatus || '').toLowerCase();
            const sessionComplete = sessionStatus === 'complete' || sessionStatus === 'processing';

            if (responseCode === 'ORDER_EXPIRED' || responseCode === 'HOLDS_EXPIRED') {
              if (!cancelled) {
                setError(
                  responseMessage ||
                  'One or more ticket holds expired. Please return to your cart and try again.'
                );
                setStatusMessage('');
              }
              return;
            }

            if (!orderPaid && sessionComplete && pollAttempts < MAX_POLL_ATTEMPTS) {
              shouldRepoll = true;
              setStatusMessage('Payment confirmed by Stripe. Finalizing your tickets…');
            } else {
              const fallbackOrderId = response.orderId || resolvedOrderIdRef.current;
              if (fallbackOrderId) {
                try {
                  const fallbackOrder = await api.get(`/orders/${fallbackOrderId}`);
                  if (!cancelled) {
                    setOrderData(fallbackOrder);
                  }
                } catch (orderErr) {
                  console.error('Failed to load order after payment mismatch:', orderErr);
                }
              }
              if (!cancelled) {
                setError(
                  responseMessage ||
                  'Payment verification failed. Please contact support with your order ID.'
                );
                setStatusMessage('');
              }
            }
          }
        } else if (orderId) {
          const orderResponse = await api.get(`/orders/${orderId}`);
          if (cancelled) return;
          setOrderData(orderResponse);
          await loadCartFromBackend();
          setStatusMessage('');
        } else {
          navigate('/', { replace: true });
          return;
        }
      } catch (error) {
        console.error('Checkout error detail:', error);
        if (!cancelled) {
          let errMsg = 'Connection failed';

          if (error.response) {
            const status = error.response.status;
            const data = error.response.data || {};

            // Handle specific business errors (409 Conflict)
            if (status === 409 || data.code === 'HOLDS_EXPIRED' || data.code === 'ORDER_EXPIRED') {
              errMsg = data.error || 'Your ticket reservation has expired. Please return to your cart and try again.';
              // We can identify this as a soft error if needed
            } else {
              // Standard Axios error with response
              errMsg = data.error || `Server Error (${status})`;
            }
          } else if (error.message) {
            // Standard Error object
            errMsg = error.message;
          } else if (error.error) {
            // API Client unwrapped error object { error: "..." }
            errMsg = error.error;
          } else if (typeof error === 'string') {
            errMsg = error;
          } else {
            // Fallback for unknown objects
            errMsg = 'An unexpected error occurred during checkout verification.';
          }

          setError(errMsg);
          setStatusMessage('');
        }
      } finally {
        if (!shouldRepoll && !cancelled) {
          setIsLoading(false);
        }
      }

      if (shouldRepoll && !cancelled) {
        timeoutId = setTimeout(() => setPollAttempts((prev) => prev + 1), POLL_INTERVAL);
      }
    };

    handleCheckoutSuccess();

    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [sessionId, orderId, navigate, loadCartFromBackend, pollAttempts]);

  const handleViewEvents = () => navigate('/events');
  const handleViewProfile = () => navigate('/profile');

  const calculateTicketCount = () => {
    const items = orderData?.orderItems || orderData?.items || [];
    return items.reduce((total, item) => total + (item.quantity || 0), 0);
  };

  const handleDownloadTickets = async () => {
    setDownloadError(null);
    const orderIdForDownload = orderData?.id || orderData?.orderId || displayOrderId;
    if (!orderIdForDownload) {
      setDownloadError('Tickets are still finalizing. Please refresh in a moment.');
      return;
    }

    const token = localStorage.getItem('authToken');
    if (!token) {
      setDownloadError('Please sign in to download your tickets.');
      return;
    }

    try {
      setDownloadingTickets(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/tickets/order/${orderIdForDownload}/download`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Download failed (${response.status})`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `order-${String(orderIdForDownload).slice(0, 8)}-tickets.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (downloadErr) {
      console.error('Ticket download error:', downloadErr);
      setDownloadError('Unable to download tickets right now. Please try again shortly.');
    } finally {
      setDownloadingTickets(false);
    }
  };

  /* --- LOADING STATE --- */
  if (isLoading) {
    return (
      <PageShell>
        <ContentCard style={{ alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
          <Loader2 size={48} className="spin" color="#3b82f6" style={{ animation: 'spin 1s linear infinite' }} />
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 600, marginTop: '1rem' }}>Finalizing Order</h2>
          <p style={{ color: palette.muted, margin: 0 }}>
            {statusMessage || 'Verifying your payment...'}
          </p>
        </ContentCard>
      </PageShell>
    );
  }

  /* --- ERROR STATE --- */
  if (error) {
    return (
      <PageShell>
        <ContentCard>
          <HeaderSection>
            <IconBadge $type="error">
              <AlertTriangle />
            </IconBadge>
            <Title style={{ background: 'none', WebkitTextFillColor: '#fff' }}>We hit a snag</Title>
            <Subtitle>{error}</Subtitle>
            {displayOrderId && (
              <span style={{ fontSize: '0.9rem', color: palette.muted, background: 'rgba(255,255,255,0.05)', padding: '0.5rem 1rem', borderRadius: '12px' }}>
                Ref: {displayOrderId}
              </span>
            )}
          </HeaderSection>

          <ActionRow style={{ gridTemplateColumns: '1fr' }}>
            <Button $variant="primary" onClick={handleViewEvents} style={{ width: '100%' }}>
              Browse Events <ArrowRight size={18} />
            </Button>
            <Button onClick={() => navigate('/support')} style={{ width: '100%' }}>
              Contact Support
            </Button>
          </ActionRow>
        </ContentCard>
      </PageShell>
    );
  }

  if (!orderData) return null;

  /* --- SUCCESS STATE --- */
  return (
    <PageShell>
      <Helmet>
        <title>Order Confirmed - TikPlat</title>
      </Helmet>

      <ContentCard>
        <HeaderSection>
          <IconBadge>
            <CheckCircle />
          </IconBadge>
          <Title>You're all set!</Title>
          <Subtitle>
            Your order is confirmed. We've sent a confirmation email to <strong>{orderData.buyerEmail || orderData.customerEmail}</strong>.
          </Subtitle>
        </HeaderSection>

        <OrderSummaryCard>
          <SummaryHeader>
            <h2><Ticket size={20} color={palette.accent} /> Order Summary</h2>
            <StatusPill $status={orderData.status || 'PAID'}>{orderData.status || 'CONFIRMED'}</StatusPill>
          </SummaryHeader>

          <DataGrid>
            <DataItem>
              <label><CreditCard size={14} /> Order Number</label>
              <span style={{ fontFamily: 'monospace', letterSpacing: '0.05em' }}>
                {orderData.orderNumber || orderData.id?.slice(0, 8).toUpperCase()}
              </span>
            </DataItem>

            <DataItem>
              <label><Ticket size={14} /> Tickets</label>
              <span>{calculateTicketCount()} Tickets</span>
            </DataItem>

            <DataItem>
              <label><Mail size={14} /> Sent To</label>
              <span style={{ fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {orderData.buyerEmail || orderData.customerEmail}
              </span>
            </DataItem>

            <DataItem>
              <label><Calendar size={14} /> Date</label>
              <span>{new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
            </DataItem>
          </DataGrid>

          <TotalRow>
            <span>Total Paid</span>
            <span>€{orderData.totalAmount?.toFixed(2) || '0.00'}</span>
          </TotalRow>
        </OrderSummaryCard>

        <InfoBox>
          <Clock size={20} color={palette.accent} />
          <span>
            <strong>Digital tickets unlock 24 hours before the event.</strong><br />
            Please bring a valid photo ID matching the name on your ticket. Arrive 30 minutes early to avoid queues.
          </span>
        </InfoBox>

        <ActionRow>
          <Button $variant="primary" onClick={handleDownloadTickets} disabled={downloadingTickets}>
            {downloadingTickets ? <Loader2 className="spin" /> : <Download size={20} />}
            {downloadingTickets ? 'Preparing...' : 'Download Tickets'}
          </Button>
          <Button onClick={handleViewProfile}>
            View My Tickets
          </Button>
        </ActionRow>

        {downloadError && (
          <div style={{ color: '#ef4444', fontSize: '0.9rem', textAlign: 'center', background: 'rgba(239, 68, 68, 0.1)', padding: '0.75rem', borderRadius: '12px' }}>
            {downloadError}
          </div>
        )}

        <SupportLink onClick={() => navigate('/support')}>
          Need help with your order?
        </SupportLink>

      </ContentCard>
    </PageShell>
  );
};

export default CheckoutSuccessPage;
